import React, { useEffect, useLayoutEffect } from 'react';
import { DataGrid, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import { arSD, ruRU, trTR, enUS } from '@mui/x-data-grid/locales';
import { Paper } from '@mui/material';
import { SCUIButton } from '../../atoms';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExcelJS from 'exceljs';
import { t } from 'i18next';

/**
 * MUI X DataGrid v8 changed GridRowSelectionModel from GridRowId[] to
 * { type: 'include' | 'exclude', ids: Set<GridRowId> }.
 * These helpers bridge callers that still use the legacy array format.
 */
const toSelectionModel = (model: any): GridRowSelectionModel | undefined => {
    if (model === undefined || model === null) return undefined;
    if (Array.isArray(model)) return { type: 'include', ids: new Set(model) };
    return model;
};

const DATA_GRID_LOCALES: Record<string, typeof enUS> = {
    'en-US': enUS,
    'ar-AR': arSD,
    'ru-RU': ruRU,
    'tr-TR': trTR,
};

const resolveLocaleText = (language: string, customNoRowsLabel?: string) => {
    const localePack = DATA_GRID_LOCALES[language];
    if (!localePack) {
        return null;
    }

    const defaultText = localePack.components.MuiDataGrid.defaultProps.localeText;
    return {
        ...defaultText,
        noRowsLabel: customNoRowsLabel ? customNoRowsLabel : defaultText.noRowsLabel,
    };
};

const calculateColumnWidths = (data: any[]) =>
    Object.keys(data[0]).map((key) => {
        const columnData = data.map((row: any) => String(row[key] || '').length);
        const maxLength = Math.max(...columnData);
        return maxLength + 2;
    });

const downloadExcel = (props: any) => {
    const data = props.excelDownloadData || props.rows;
    const folderName = props.excelDownloadFolderName || 'ExcelData';
    const hiddenColumns = props.hiddenExcelColumn || [];
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sayfa-1');
    const columnWidths = calculateColumnWidths(data);

    sheet.columns = Object.keys(data[0]).map((key, index) => {
        const width = columnWidths[index] + (index === 1 ? 5 : 0);
        return {
            header: key,
            key: key,
            width: width > 10 ? width : 10,
            hidden: hiddenColumns.includes(key),
        };
    });

    data.forEach((row: any) => sheet.addRow(row));

    sheet.eachRow((row) => {
        row.eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });
    });

    const today = new Date();
    const fileName = `${folderName} - ${today.getDate()}.${
        today.getMonth() + 1
    }.${today.getFullYear()} - ${today.getHours()}_${today.getMinutes()}.xlsx`;

    workbook.xlsx.writeBuffer().then((buffer: any) => {
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    });
};

const toLegacySelectionIds = (
    model: GridRowSelectionModel,
    rows: any[],
    getRowId?: (row: any) => any
): any[] => {
    if (!model || !('ids' in model)) {
        return [];
    }

    if (model.type === 'include') {
        return Array.from(model.ids);
    }

    // MUI v8 may represent "Select All" as exclude mode.
    // Convert exclude mode back to an explicit selected id array for legacy callers.
    const resolveId = getRowId ? getRowId : (row: any) => row?.id;
    return (rows || [])
        .map((row: any) => resolveId(row))
        .filter((id: any) => !model.ids.has(id));
};

const getRowSelectionModelProps = (props: any) => {
    if (props.rowSelectionModel === undefined && props.selectionModel === undefined) {
        return {};
    }

    return {
        rowSelectionModel: toSelectionModel(props.rowSelectionModel ?? props.selectionModel),
    };
};

const getRowSelectionChangeProps = (props: any) => {
    const handler = props.onRowSelectionModelChange ?? props.onSelectionModelChange;
    if (!handler) {
        return {};
    }

    return {
        onRowSelectionModelChange: (model: GridRowSelectionModel) => {
            handler(toLegacySelectionIds(model, props.rows, props.getRowId));
        },
    };
};

const resolveGetRowId = (props: any) => {
    if (props.getRowId) {
        return props.getRowId;
    }

    if (props.randomId) {
        // Stable per-row ids for the lifetime of each row object.
        // Generating a new random id on every getRowId call breaks filtering/paging.
        const idCache = new WeakMap<object, number>();
        return (row: any) => {
            if (row == null || typeof row !== 'object') {
                return row;
            }
            const cached = idCache.get(row);
            if (cached !== undefined) {
                return cached;
            }
            const array = new Uint32Array(1);
            window.crypto.getRandomValues(array);
            idCache.set(row, array[0]);
            return array[0];
        };
    }

    return (row: any) => row?.id;
};

const resolveRowCount = (props: any, isServerMode: boolean) => {
    if (props.rowCount !== undefined) {
        return props.rowCount;
    }

    if (isServerMode || props.sortingMode === 'server') {
        return props.rows?.length;
    }

    return undefined;
};

const buildLegacyHiddenColumnsVisibility = (columns: any) => {
    const visibility: Record<string, boolean> = {};
    if (!Array.isArray(columns)) {
        return visibility;
    }

    columns.forEach((col: any) => {
        if (col?.field && col.hide) {
            visibility[col.field] = false;
        }
    });
    return visibility;
};

const stripLegacyHideFromColumns = (columns: any) => {
    if (!Array.isArray(columns)) {
        return columns;
    }

    return columns.map((col: any) => {
        if (!col?.hide) {
            return col;
        }
        const rest = { ...col };
        delete rest.hide;
        return rest;
    });
};

const persistColumnVisibilityIfChanged = (
    newColumnVisibilityModel: any,
    onColumnVisibilityModelChange?: (model: any) => void
) => {
    const savedModel = localStorage.getItem('columnVisibilityModel');
    const oldColumnVisibilityModel = savedModel ? JSON.parse(savedModel) : {};
    if (JSON.stringify(newColumnVisibilityModel) === JSON.stringify(oldColumnVisibilityModel)) {
        return;
    }

    localStorage.setItem('columnVisibilityModel', JSON.stringify(newColumnVisibilityModel));
    onColumnVisibilityModelChange?.(newColumnVisibilityModel);
};

const buildPaperStyle = (style: any) => ({
    ...style,
    boxShadow: 0,
    marginTop: style?.marginTop != undefined ? style.marginTop : '2rem',
    width: '100%',
});

const shouldShowExcelDownloadButton = (props: any) =>
    Boolean(
        props?.excelDownloadButton &&
            Array.isArray(props?.excelDownloadData) &&
            props.excelDownloadData.length > 0
    );

const CustomDataGrid = styled(DataGrid)(() => ({
    '.MuiDataGrid-menuIcon': {
        visibility: 'visible',
    },
}));

const getRowsIdSignature = (rows: any[] | undefined, getRowId?: (row: any) => any) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return '0';
    }

    const resolveId = getRowId ?? ((row: any) => row?.id);
    // Signature must change whenever the visible row-id set changes (e.g. client-side search).
    return `${rows.length}:${rows.map((row) => String(resolveId(row) ?? '')).join('|')}`;
};

export const SCUICustomDataGrid = (props: any) => {
    const { i18n } = useTranslation();
    const [pageSize, setPageSize] = React.useState<number>(props.pageSize ? props.pageSize : 10);
    const [pageNumber, setPageNumber] = React.useState<number>(typeof props.page === 'number' ? props.page : 0);
    const [lang, setLang] = React.useState(trTR.components.MuiDataGrid.defaultProps.localeText);

    const rows = React.useMemo(
        () => (Array.isArray(props.rows) ? props.rows : []),
        [props.rows]
    );

    const getRowId = React.useMemo(() => resolveGetRowId(props), [props.getRowId, props.randomId]);

    const rowsIdSignature = React.useMemo(
        () => getRowsIdSignature(rows, getRowId),
        [rows, getRowId]
    );

    useEffect(() => {
        const nextLocaleText = resolveLocaleText(i18n.language, props.localeText);
        if (nextLocaleText) {
            setLang(nextLocaleText);
        }
    }, [i18n.language, props.localeText]);

    const savedColumnVisibilityModel = React.useMemo(() => {
        if (!props.isHeadersCached) {
            return {};
        }
        const savedModel = localStorage.getItem('columnVisibilityModel');
        return savedModel ? JSON.parse(savedModel) : {};
    }, [props.isHeadersCached]);

    const legacyHiddenColumnsVisibility = React.useMemo(
        () => buildLegacyHiddenColumnsVisibility(props.columns),
        [props.columns]
    );

    const mergedColumnVisibilityModel = React.useMemo(() => {
        const baseModel =
            props.initialState?.columns?.columnVisibilityModel || {};
        const cachedModel = props.isHeadersCached ? savedColumnVisibilityModel : {};
        const combined = {
            ...legacyHiddenColumnsVisibility,
            ...baseModel,
            ...cachedModel,
        };
        return Object.keys(combined).length > 0 ? combined : undefined;
    }, [legacyHiddenColumnsVisibility, props.initialState, props.isHeadersCached, savedColumnVisibilityModel]);

    const computedColumns = React.useMemo(
        () => stripLegacyHideFromColumns(props.columns),
        [props.columns]
    );

    const initialState = React.useMemo(
        () => ({
            ...props.initialState,
            columns: {
                ...props.initialState?.columns,
                columnVisibilityModel: mergedColumnVisibilityModel,
            },
        }),
        [props.initialState, mergedColumnVisibilityModel]
    );

    const isServerMode = !!props.paginationModeChange;

    useEffect(() => {
        if (isServerMode && typeof props.page === 'number') {
            setPageNumber(props.page);
        }
    }, [isServerMode, props.page]);

    useEffect(() => {
        if (typeof props.pageSize === 'number') {
            setPageSize(props.pageSize);
        }
    }, [props.pageSize]);

    // External row replacements (search/filter) are not DataGrid filterModel changes,
    // so controlled pagination can keep a stale page / row cache. Reset to first page.
    useLayoutEffect(() => {
        if (isServerMode || props.allrecord) {
            return;
        }
        setPageNumber(0);
    }, [rowsIdSignature, isServerMode, props.allrecord]);

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        const currentPage = typeof props.page === 'number' ? props.page : pageNumber;
        const currentPageSize = typeof props.pageSize === 'number' ? props.pageSize : pageSize;

        if (!isServerMode) {
            setPageNumber(model.page);
            setPageSize(model.pageSize);
        }

        if (props.onPageChange && (!isServerMode || model.page !== currentPage)) {
            props.onPageChange(model.page);
        }
        if (props.onPageSizeChange && (!isServerMode || model.pageSize !== currentPageSize)) {
            props.onPageSizeChange(model.pageSize);
        }
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    backgroundColor: 'transparent',
                }}
            >
                {shouldShowExcelDownloadButton(props) && (
                    <SCUIButton
                        type={'primary'}
                        sizeType={'fixed'}
                        size={'s'}
                        text={props?.excelDownloadButtonName || t('general.exportToExcel')}
                        showStartIcon
                        startIcon={<FileDownloadIcon style={{ color: 'white' }} />}
                        onClick={() => downloadExcel(props)}
                    />
                )}
            </div>
            <Paper style={buildPaperStyle(props.style)}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <CustomDataGrid
                        // Remount when the visible row-id set changes so MUI row cache cannot stay stale
                        // after parent-side filtering (e.g. search textfield -> filtered rows prop).
                        key={isServerMode ? undefined : rowsIdSignature}
                        getRowClassName={props.getRowClassName}
                        checkboxSelection={props.checkboxSelection}
                        {...getRowSelectionModelProps(props)}
                        {...getRowSelectionChangeProps(props)}
                        getRowId={getRowId}
                        sx={{
                            ...(props.sx ?? {}),
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-cell .MuiTypography-root': {
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                            },
                        }}
                        localeText={props.localText ? props.localText : lang}
                        disableRowSelectionOnClick={props.selectionOnClick ? props.selectionOnClick : true}
                        rows={rows}
                        columns={computedColumns}
                        hideFooter={props.hideFooter || props.allrecord}
                        rowHeight={props.rowHeight ? props.rowHeight : 50}
                        initialState={initialState}
                        pageSizeOptions={props.allrecord ? [] : [5, 10, 20, 50, 100]}
                        paginationMode={isServerMode ? 'server' : 'client'}
                        sortingMode={props.sortingMode ? props.sortingMode : 'client'}
                        rowCount={resolveRowCount(props, isServerMode)}
                        loading={Boolean(props.loading)}
                        {...(!props.allrecord && { pagination: true })}
                        autoHeight
                        paginationModel={
                            props.allrecord
                                ? undefined
                                : {
                                      page: pageNumber,
                                      pageSize: pageSize,
                                  }
                        }
                        onPaginationModelChange={!props.allrecord ? handlePaginationModelChange : undefined}
                        onCellClick={props.onCellClick}
                        onSortModelChange={props.onSortModelChange}
                        {...(props.slotProps ? { slotProps: props.slotProps } : {})}
                        onStateChange={(state) => {
                            if (!props.isHeadersCached) return;
                            persistColumnVisibilityIfChanged(
                                state.columns.columnVisibilityModel,
                                props.onColumnVisibilityModelChange
                            );
                        }}
                    />
                </div>
            </Paper>
        </>
    );
};
